import {ClassComponent, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Link from 'flarum/common/components/Link';
import ItemList from 'flarum/common/utils/ItemList';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import extractText from 'flarum/common/utils/extractText';
import listItems from 'flarum/common/helpers/listItems';
import EditAlbumModal from '../modals/EditAlbumModal';
import Album from '../models/Album';
import {cimaiseAlbum} from '../routes';

const translationPrefix = 'kilowhat-cimaise.forum.album-thumbnail.';

interface AlbumThumbnailAttrs {
    album: Album
    showUser: boolean
    ondelete: () => void
    onafteredit?: () => void
}

export default class AlbumThumbnail implements ClassComponent<AlbumThumbnailAttrs> {
    deleting: boolean = false

    controls(album: Album, vnode: Vnode<AlbumThumbnailAttrs, this>) {
        const items = new ItemList();

        if (album.canEdit()) {
            items.add('edit', Button.component({
                className: 'Button',
                onclick: () => {
                    app.modal.show(EditAlbumModal, {
                        album,
                        ondelete: () => {
                            if (vnode.attrs.onafteredit) {
                                vnode.attrs.onafteredit();
                            }
                        },
                    });
                },
            }, app.translator.trans(translationPrefix + 'edit')));
        }

        if (album.isHidden() && album.canRestore()) {
            items.add('hide', Button.component({
                loading: this.deleting,
                onclick: () => {
                    this.deleting = true;

                    album.save({
                        isHidden: false,
                    }).then(() => {
                        this.deleting = false;
                        m.redraw();
                    }).catch(err => {
                        this.deleting = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'restore')));
        }

        if (!album.isHidden() && album.canHide()) {
            items.add('hide', Button.component({
                loading: this.deleting,
                onclick: () => {
                    this.deleting = true;

                    album.save({
                        isHidden: true,
                    }).then(() => {
                        this.deleting = false;
                        m.redraw();
                    }).catch(err => {
                        this.deleting = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'hide')));
        }

        if (album.canDelete() && album.isHidden()) {
            items.add('delete', Button.component({
                loading: this.deleting,
                onclick: () => {
                    if (confirm(extractText(app.translator.trans(translationPrefix + 'delete-confirmation', {
                        title: album.title(),
                    })))) {
                        this.deleting = true;

                        album.delete().then(() => {
                            this.deleting = false;

                            vnode.attrs.ondelete();
                            m.redraw();
                        }).catch(err => {
                            this.deleting = false;
                            m.redraw();
                            throw err;
                        });
                    }
                },
            }, app.translator.trans(translationPrefix + 'delete')));
        }

        return items;
    }

    view(vnode: Vnode<AlbumThumbnailAttrs, this>) {
        const {album} = vnode.attrs;

        const controls = this.controls(album, vnode).toArray();

        const href = cimaiseAlbum(album);

        const badges = album.badges().toArray();

        const coverPicture = album.coverPicture();

        return m('.AlbumThumbnail', [
            controls.length ? Dropdown.component({
                className: 'AlbumThumbnail-controls',
                menuClassName: 'Dropdown-menu--right',
                buttonClassName: 'Button Button--icon Button--flat',
                label: app.translator.trans(translationPrefix + 'controls'),
                icon: 'fas fa-ellipsis-v',
            }, controls) : null,
            badges.length ? m('ul.AlbumThumbnail-badges badges', listItems(badges)) : null,
            m(Link, {
                href,
            }, coverPicture ? m('img.AlbumThumbnail--picture', {
                src: coverPicture.imageUrl(150),
            }) : m('.ImagePlaceholder')),
            m('.AlbumThumbnail-meta', [
                m(Link, {
                    className: 'AlbumThumbnail-title',
                    href,
                    title: album.title(),
                }, album.title()),
                vnode.attrs.showUser ? m('.AlbumThumbnail-user', [
                    app.translator.trans(translationPrefix + 'by-user'),
                    ' ',
                    avatar(album.user() || null),
                    username(album.user()),
                ]) : null,
            ]),
        ]);
    }
}
